import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaCheckCircle, FaTimes, FaEye,
  FaUser, FaBuilding, FaWeightHanging, FaMapMarkerAlt,
  FaMoneyBillWave, FaArrowLeft, FaCalendarAlt,
  FaFileAlt, FaExclamationTriangle, FaClock
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import ConfirmDialog from '../components/ConfirmDialog';

function ClientQuoteApproval({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState({});
  const [clientId, setClientId] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientData(userData);
  }, []);

  const fetchClientData = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const id = data.clients[0].id;
        setClientId(id);
        fetchJobs(id);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
      setLoading(false);
    }
  };

  const fetchJobs = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`/jobs/client/${id}`);
      const data = await response.json();
      if (data.success) {
        const quoteJobs = data.jobs.filter(j => 
          j.admin_quote_to_client && 
          j.client_quote_status === 'pending' &&
          j.status !== 'completed' &&
          j.status !== 'cancelled'
        );
        setJobs(quoteJobs);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveQuote = async (job) => {
    setProcessing(true);
    setSelectedJob(job);
    try {
      const response = await fetch(`/jobs/${job.id}/client-approve-quote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          client_quote_status: 'approved',
          client_quote_approved_at: new Date().toISOString()
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Quote approved! Your job will now be processed.');
        setShowModal(false);
        fetchJobs(clientId);
      } else {
        toast.error(data.error || 'Failed to approve quote');
      }
    } catch (err) {
      console.error('Error approving quote:', err);
      toast.error('Failed to approve quote');
    } finally {
      setProcessing(false);
      setSelectedJob(null);
    }
  };

  const [rejectTarget, setRejectTarget] = useState(null); // job to reject

  const handleRejectQuote = (job) => {
    setRejectTarget(job);
  };

  const performRejectQuote = async () => {
    if (!rejectTarget) return;
    setProcessing(true);
    try {
      const response = await fetch(`/jobs/${rejectTarget.id}/client-reject-quote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          client_quote_status: 'rejected',
          status: 'cancelled'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('❌ Quote rejected. Job cancelled.');
        fetchJobs(clientId);
      } else {
        toast.error(data.error || 'Failed to reject quote');
      }
    } catch (err) {
      console.error('Error rejecting quote:', err);
      toast.error('Failed to reject quote');
    } finally {
      setProcessing(false);
    }
    setRejectTarget(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  if (loading) {
    return (
      // ✅ FIXED: activeTab="quotes"
      <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading quotes...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    // ✅ FIXED: activeTab="quotes"
    <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/client-dashboard')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: '#0A1929' }}
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📋 Quote Approval</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {jobs.length} jobs waiting for your approval
          </p>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Quotes Pending</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>All your job quotes have been approved.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{job.job_number}</h3>
                    <span className={getStatusBadge(job.client_quote_status)}>
                      {job.client_quote_status === 'pending' ? 'Awaiting Approval' : job.client_quote_status}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#4A5568' }}>
                    <FaBuilding className="inline mr-1" style={{ color: '#00A86B' }} />
                    {job.commodity} • {job.total_quantity_mt} MT
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm mt-2" style={{ color: '#4A5568' }}>
                    <span className="flex items-center gap-1">
                      <FaMapMarkerAlt size={14} /> {job.origin} → {job.destination}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt size={14} /> {job.start_date || 'TBD'}
                    </span>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium" style={{ color: '#0A1929' }}>
                      <FaMoneyBillWave className="inline mr-2" style={{ color: '#00A86B' }} />
                      Quote: <span className="font-bold" style={{ color: '#00A86B' }}>R{job.admin_quote_to_client}/MT</span>
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                      Total: R{(job.admin_quote_to_client * job.total_quantity_mt).toFixed(2)}
                    </p>
                  </div>
                  {job.client_notes && (
                    <p className="mt-2 text-sm" style={{ color: '#4A5568' }}>
                      <span className="font-medium">Your Notes:</span> {job.client_notes}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => {
                      setSelectedJob(job);
                      setShowModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors"
                  >
                    <FaCheckCircle size={14} /> Review & Approve
                  </button>
                  <button 
                    onClick={() => handleRejectQuote(job)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                  >
                    <FaTimes size={14} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showModal && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0A1929' }}>Confirm Quote Approval</h2>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm" style={{ color: '#4A5568' }}>
                <strong>Job:</strong> {selectedJob.job_number}
              </p>
              <p className="text-sm" style={{ color: '#4A5568' }}>
                <strong>Commodity:</strong> {selectedJob.commodity}
              </p>
              <p className="text-sm" style={{ color: '#4A5568' }}>
                <strong>Quantity:</strong> {selectedJob.total_quantity_mt} MT
              </p>
              <p className="text-sm font-bold mt-2" style={{ color: '#00A86B' }}>
                <strong>Quote:</strong> R{selectedJob.admin_quote_to_client}/MT
              </p>
              <p className="text-sm font-bold" style={{ color: '#00A86B' }}>
                <strong>Total:</strong> R{(selectedJob.admin_quote_to_client * selectedJob.total_quantity_mt).toFixed(2)}
              </p>
            </div>
            <p className="text-sm mb-4" style={{ color: '#4A5568' }}>
              Approving this quote will send the job to our logistics team for processing.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleApproveQuote(selectedJob)}
                disabled={processing}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#00A86B' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
              >
                <FaCheckCircle size={14} />
                {processing ? 'Processing...' : '✅ Approve Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Reject Quote"
        message="Are you sure you want to reject this quote? This will cancel the job."
        confirmLabel="Reject"
        danger
        onConfirm={performRejectQuote}
      />
    </ClientLayout>
  );
}

export default ClientQuoteApproval;
